import api from "@/api/v1-jwt/config/api";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import notificationQueryKeys from "./query-keys";
import { NotificationInfoResponse, NotificationSubject } from "./types";

type NotificationInfoQueryKey = ReturnType<
  typeof notificationQueryKeys.info
>;

interface GetNotificationInfoParams {
  userId: string;
  subject?: NotificationSubject;
}

const getNotificationInfo = async ({
  userId,
  subject,
}: GetNotificationInfoParams): Promise<NotificationInfoResponse> => {
  const subjectParam = subject ? `?subject=${subject}` : "";
  const url = `/platform/notifications/${userId}/info${subjectParam}`;
  const { data } = await api.get(url);
  return data.data || [];
};

export const useNotificationInfoOptions = <TSelected = NotificationInfoResponse>(
  { userId, subject }: GetNotificationInfoParams,
  options?: Omit<
    UseQueryOptions<NotificationInfoResponse, Error, TSelected, NotificationInfoQueryKey>,
    "queryKey" | "queryFn"
  >
): UseQueryOptions<
  NotificationInfoResponse,
  Error,
  TSelected,
  NotificationInfoQueryKey
> => {
  const defaultOptions: UseQueryOptions<
    NotificationInfoResponse,
    Error,
    TSelected,
    NotificationInfoQueryKey
  > = {
    queryKey: notificationQueryKeys.info(userId, subject),
    queryFn: () => getNotificationInfo({ userId, subject }),
    staleTime: 60 * 60 * 1000,
    enabled: options?.enabled ?? !!userId,
  };
  return { ...defaultOptions, ...options };
};

export const useNotificationInfo = <TSelected = NotificationInfoResponse>(
  { userId, subject }: GetNotificationInfoParams,
  options?: Omit<
    UseQueryOptions<NotificationInfoResponse, Error, TSelected, NotificationInfoQueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const queryOptions = useNotificationInfoOptions<TSelected>(
    { userId, subject },
    options
  );
  return useQuery<NotificationInfoResponse, Error, TSelected, NotificationInfoQueryKey>({
    ...queryOptions,
  });
};
